import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  useScrollTrigger,
} from '@mui/material';

function ElevationScroll({ children }) {
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  return React.cloneElement(children, {
    elevation: trigger ? 4 : 0,
    style: {
      background: trigger 
        ? 'rgba(255, 255, 255, 0.95)' 
        : 'rgba(255, 255, 255, 0.1)',
      backdropFilter: trigger ? 'blur(20px)' : 'blur(10px)',
      borderBottom: trigger 
        ? '1px solid rgba(255, 49, 48, 0.1)' 
        : '1px solid transparent',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: trigger 
        ? '0 8px 32px rgba(0, 0, 0, 0.1)' 
        : 'none',
    },
  });
}

function Header() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const headerVariants = {
    hidden: { opacity: 0, y: -50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const logoVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.6, delay: 0.2, ease: "easeOut" }
    }
  };

  const buttonVariants = {
    hidden: { opacity: 0, x: 30 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.6, delay: 0.4, ease: "easeOut" }
    }
  };

  if (!mounted) return null;

  return (
    <ElevationScroll>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={headerVariants}
      >
        <AppBar position="fixed" color="transparent">
          <Container maxWidth="lg">
            <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
              <motion.div variants={logoVariants}>
                <Typography
                  variant="h4"
                  component="div"
                  sx={{
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, var(--color-brand-primary), var(--color-brand-dark))',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    letterSpacing: '-0.02em',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      filter: 'brightness(1.1)',
                    },
                  }}
                >
                  Kazi AI
                </Typography>
              </motion.div>
              
              <motion.div variants={buttonVariants}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    href="https://app.kaziai.me"
                    component={motion.a}
                    whileHover={{ 
                      scale: 1.05,
                      boxShadow: '0 8px 25px rgba(255, 49, 48, 0.2)'
                    }}
                    whileTap={{ scale: 0.95 }}
                    sx={{
                      borderColor: 'var(--color-brand-primary)',
                      color: 'var(--color-brand-primary)',
                      borderRadius: '12px',
                      fontWeight: 600,
                      px: 3,
                      py: 1,
                      backdropFilter: 'blur(10px)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 49, 48, 0.3)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        borderColor: 'var(--color-brand-dark)',
                        backgroundColor: 'rgba(255, 49, 48, 0.1)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    Sign In
                  </Button>
                  <Button
                    variant="contained"
                    href="https://app.kaziai.me"
                    component={motion.a}
                    whileHover={{ 
                      scale: 1.05,
                      boxShadow: '0 12px 30px rgba(255, 49, 48, 0.4)'
                    }}
                    whileTap={{ scale: 0.95 }}
                    sx={{
                      background: 'linear-gradient(135deg, var(--color-brand-primary), var(--color-brand-dark))',
                      borderRadius: '12px',
                      fontWeight: 600,
                      px: 3,
                      py: 1,
                      boxShadow: '0 4px 15px rgba(255, 49, 48, 0.3)',
                      border: 'none',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, var(--color-brand-light), var(--color-brand-primary))',
                        opacity: 0,
                        transition: 'opacity 0.3s',
                      },
                      '&:hover::before': {
                        opacity: 1,
                      },
                    }}
                  >
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                      Get Started
                    </Box>
                  </Button>
                </Box>
              </motion.div>
            </Toolbar>
          </Container>
        </AppBar>
      </motion.div>
    </ElevationScroll>
  );
}

export default Header; 